import { createContext, useContext, useReducer, Dispatch, ReactNode } from 'react';
import { DefaultDialogProps } from '../default-dialog';
import { DefaultMessageProps } from '../default-message';
import { DefaultBackdropProps } from '../default-backdrop';

// State interface
export interface BaseLayoutState {
    dialogProps: DefaultDialogProps;
    messageProps: DefaultMessageProps;
    loadingProps: DefaultBackdropProps;
}

// Action types
export enum ACTION_TYPES {
    SHOW_DIALOG = 'SHOW_DIALOG',
    HIDE_DIALOG = 'HIDE_DIALOG',
    SHOW_MESSAGE = 'SHOW_MESSAGE',
    HIDE_MESSAGE = 'HIDE_MESSAGE',
    SHOW_LOADING = 'SHOW_LOADING',
    HIDE_LOADING = 'HIDE_LOADING',
}

// Action interfaces
export type BaseLayoutAction =
    | { type: ACTION_TYPES.SHOW_DIALOG; payload: DefaultDialogProps }
    | { type: ACTION_TYPES.HIDE_DIALOG }
    | { type: ACTION_TYPES.SHOW_MESSAGE; payload: DefaultMessageProps }
    | { type: ACTION_TYPES.HIDE_MESSAGE }
    | { type: ACTION_TYPES.SHOW_LOADING; payload: DefaultBackdropProps }
    | { type: ACTION_TYPES.HIDE_LOADING };

// Initial state
const initialState: BaseLayoutState = {
    dialogProps: { open: false } as DefaultDialogProps,
    messageProps: { open: false } as DefaultMessageProps,
    loadingProps: { open: false } as DefaultBackdropProps,
};

// Reducer
function baseLayoutReducer(state: BaseLayoutState, action: BaseLayoutAction): BaseLayoutState {
    switch (action.type) {
        case ACTION_TYPES.SHOW_DIALOG:
            return {
                ...state,
                dialogProps: {
                    ...action.payload,
                    open: true,
                },
            };
        case ACTION_TYPES.HIDE_DIALOG:
            return {
                ...state,
                dialogProps: { ...state.dialogProps, open: false },
            };
        case ACTION_TYPES.SHOW_MESSAGE:
            return {
                ...state,
                messageProps: {
                    ...action.payload,
                    open: true,
                },
            };
        case ACTION_TYPES.HIDE_MESSAGE:
            return {
                ...state,
                messageProps: { ...state.messageProps, open: false },
            };
        case ACTION_TYPES.SHOW_LOADING:
            return {
                ...state,
                loadingProps: action.payload,
            };
        case ACTION_TYPES.HIDE_LOADING:
            return {
                ...state,
                loadingProps: { open: false } as DefaultBackdropProps,
            };
        default:
            return state;
    }
}

// Context
const BaseLayoutStateContext = createContext<BaseLayoutState | undefined>(undefined);
const BaseLayoutDispatchContext = createContext<Dispatch<BaseLayoutAction> | undefined>(undefined);

// Provider
export function BaseLayoutProvider({ children }: { children: ReactNode }) {
    const [state, dispatch] = useReducer(baseLayoutReducer, initialState);

    return (
        <BaseLayoutStateContext.Provider value={state}>
            <BaseLayoutDispatchContext.Provider value={dispatch}>
                {children}
            </BaseLayoutDispatchContext.Provider>
        </BaseLayoutStateContext.Provider>
    );
}

// Hooks to access state and dispatch
export function useBaseLayoutState() {
    const context = useContext(BaseLayoutStateContext);
    if (context === undefined) {
        throw new Error('useBaseLayoutState must be used within BaseLayoutProvider');
    }
    return context;
}

export function useBaseLayoutDispatch() {
    const context = useContext(BaseLayoutDispatchContext);
    if (context === undefined) {
        throw new Error('useBaseLayoutDispatch must be used within BaseLayoutProvider');
    }
    return context;
}
